import React, {useEffect, useRef, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import Input from '../../components/Input';
import Select from 'react-select';
import {
  useCreatePeriodInspectionMutation,
  useGetPeriodsQuery,
  useLazyDemandInspectionsQuery,
  useLazyGetAgenciesWithEquipmentsQuery,
  useLazyGetFacilityQuery,
  useLazyGetInspectionByFacilityQuery,
} from '../../store/builder/inspections.api';
import {setLoader} from '../../store/builder/reducer';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import './index.scss';
import moment from 'moment';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {Helmet} from 'react-helmet';
import {toast} from 'react-toastify';
import CreateFiltersChangeModal from '../../components/CreateFiltersChangeModal';
import useLoader from '../../global/UseLoadingHandler';
import WiFiIcon from '../../SVG/WiFiIcon';
import WebIcon from '../../SVG/WebIcon';
import {useTranslation} from '../../hooks/useTranslation';

export default function Inspections() {
  const {t} = useTranslation();
  
  const statusOptions = [
    {value: 'new', label: t('inspections.statusOptions.new')},
    {value: 'half', label: t('inspections.statusOptions.partiallyCompleted')},
    {value: 'completed', label: t('inspections.statusOptions.completed')},
  ];

  // Helper function to get translated status
  const getTranslatedStatus = (status) => {
    const statusMap = {
      'new': t('inspections.statusOptions.new'),
      'half': t('inspections.statusOptions.partiallyCompleted'),
      'completed': t('inspections.statusOptions.completed'),
    };
    return statusMap[status] || status;
  };

  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const urlParams = new URLSearchParams(window.location.search);
  const currentFbo = localStorage.getItem('current-fbo');
  const popupRef = useRef();
  const pageTab = urlParams.get('tab');
  const pageFacility = urlParams.get('facility');
  const [currentTab, setCurrentTab] = useState(
    pageTab && pageTab !== '' ? pageTab : 'daily',
  );
  const [currentStatus, setCurrentStatus] = useState(null);
  const [currentSearch, setCurrentSearch] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [facilityOptions, setFacilityOptions] = useState(null);
  const [equipmentOptions, setEquipmentOptions] = useState(null);
  const [list, setList] = useState([]);
  const [addStatus, setAddStatus] = useState(false);
  const [currentFacility, setCurrentFacility] = useState(null);
  const [createFilterStatus, setCreateFilterStatus] = useState(false);
  const [createFilterType, setCreateFilterType] = useState('');
  const [demandUpdatedData, setDemandUpdatedData] = useState([]);
  const [currentDemand, setCurrentDemand] = useState(null);
  const [filterChangeName, setFilterChangeName] = useState('');
  const [tabsList, setTabsList] = useState([]);

  const [
    getInspectionByFacility,
    {data, isError, isFetching: inspectionsIsLoading, error, isSuccess},
  ] = useLazyGetInspectionByFacilityQuery();

  const {
    data: periods,
    isFetching: periodsisLoading,
    error: periodsError,
    isSuccess: periodsIsSuccess,
  } = useGetPeriodsQuery();

  const [
    getAgenciesWithEquipment,
    {
      data: agenciesWithEquipmentData,
      isLoading: agenciesWithEquipmentLoading,
      error: agenciesWithEquipmentError,
      isSuccess: agenciesWithEquipmentIsSuccess,
    },
  ] = useLazyGetAgenciesWithEquipmentsQuery();

  const [
    getFacility,
    {
      data: facilityData,
      isError: facilityIsError,
      error: facilityError,
      isSuccess: facilityIsSuccess,
      isFetching: facilityIsLoading,
    },
  ] = useLazyGetFacilityQuery();

  const [
    createInspectionPer,
    {
      data: createInspectionPerData,
      isError: createInspectionPerIsError,
      error: createInspectionPerError,
      isSuccess: createInspectionPerIsSuccess,
      isLoading: createInspectionPerIsLoading,
    },
  ] = useCreatePeriodInspectionMutation();

  const [
    getDemand,
    {
      data: demandData,
      isError: demandIsError,
      error: demandError,
      isSuccess: demandIsSuccess,
      isFetching: demandIsLoading,
    },
  ] = useLazyDemandInspectionsQuery();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    facilityError,
    periodsError,
    createInspectionPerError,
  ]);
  useLoader([
    facilityIsLoading,
    inspectionsIsLoading,
    createInspectionPerIsLoading,
  ]);
  const [currentFacilityId, setCurrentFacilityId] = useState(null);

  const handleOutsideClick = e => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      setAddStatus(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    getInspectionByFacility(currentFbo);
  }, [currentFbo])

  useEffect(() => {
    getFacility();
    getDemand({ id: currentFbo });
  }, []);

  useEffect(() => {
    if (tabsList) {
      setCurrentTab(tabsList[0]?.value);
    }
  }, [tabsList])

  useEffect(() => {
    if (createInspectionPerIsSuccess) {
      toast.success(t('inspections.messages.inspectionCreatedSuccess'));
      setAddStatus(false);
    }
  }, [createInspectionPerIsSuccess, t]);

  const convertStringToKey = str => {
    return str.toLowerCase().split(' ').join('_');
  };

  function formatString(str) {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  useEffect(() => {
    if (demandIsSuccess) {
      setDemandUpdatedData(
        demandData?.map(el => ({
          ...el,
          modal_type: convertStringToKey(el.name),
        })),
      );
    }
    if (isSuccess) {
      setTabsList(
        data?.tabs?.map(el => ({value: el, label: formatString(el)})),
      );
    }
  }, [demandIsSuccess, isSuccess]);

  useEffect(() => {
    if (facilityOptions) {
      getInspectionByFacility(facilityOptions[0]?.value);
      setCurrentFacilityId(facilityOptions[0]?.value);
    }
  }, [facilityOptions]);

  useEffect(() => {
    if (pageFacility) {
      setCurrentFacilityId(pageFacility);
    }
  }, [pageFacility]);

  useEffect(() => {
    navigate(
      `/inspections?tab=${pageTab ? pageTab : 'daily'}${currentFacilityId ? '&facility=' + currentFacilityId : ''}`,
    );
  }, [currentFacilityId]);

  const changeTab = tabId => {
    setCurrentTab(tabId);
    // setCurrentSearch("");
  };

  useEffect(() => {
    if (facilityData) {
      setFacilityOptions([{value: 0, label: t('inspections.filters.all')}]);
      facilityData?.map(item => {
        setFacilityOptions(old => {
          if (old) {
            return [...old, {value: item?.id, label: item?.name}];
          } else {
            return [{value: item?.id, label: item?.name}];
          }
        });
        return item;
      });
    }
  }, [facilityData]);

  useEffect(() => {
    if (currentFacilityId) {
      setCurrentFacility(
        facilityData?.filter(item => item.id == currentFacilityId),
      );
    }
  }, [currentFacilityId]);

  const fullFilter = () => {
    let s = data?.inspections?.filter(item =>
      currentSearch !== ''
        ? item?.inspection_name
            ?.toLowerCase()
            ?.includes(currentSearch?.toLowerCase())
        : item,
    );
    s = s?.filter(item => {
      if (currentTab === 'annual') {
        if (
          item?.tab === 'element_change' ||
          item?.tab === 'visual_inspection' ||
          item?.tab === 'differential_pressure_calibration' ||
          item?.tab === 'nozzle_pressure_calibration'
        ) {
          return item;
        }
        return item?.tab === currentTab;
      } else {
        return item?.tab === currentTab;
      }
    });
    s = s?.filter(item =>
      currentStatus
        ? item?.status_progress === currentStatus?.value
        : item?.status_progress !== currentStatus?.value,
    );
    setList(s);
  };

  useEffect(() => {
    fullFilter();
  }, [currentSearch, currentTab, currentStatus, data]);

  const createPeriodInspectionSubmit = ({period, code}) => {
    createInspectionPer({
      period: period,
      facility_id: currentFacilityId,
      code: code,
    }).then(res => {
      navigate(`/inspections/${res?.data?.inspection_id}`);
    });
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('inspections.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={t('inspections.title')}
        showBtn={true}
        type='inspection'
        secondAction={getInspectionByFacility}
        facilityId={currentFacilityId}
        action={() => setAddStatus(!addStatus)}
      />
      {createFilterStatus && (
        <CreateFiltersChangeModal
          createFilterType={createFilterType}
          getAgenciesWithEquipment={getAgenciesWithEquipment}
          data={agenciesWithEquipmentData}
          agenciesWithEquipmentLoading={agenciesWithEquipmentLoading}
          exit={() => setCreateFilterStatus(false)}
          currentDemand={currentDemand}
          setCurrentDemand={setCurrentDemand}
          filterChangeName={filterChangeName}
        />
      )}
      {addStatus && (
        <div className='inspections__addModal' ref={popupRef}>
          {/*{*/}
          {/*  (currentFacilityId !== null && currentFacilityId !== undefined && currentFacilityId !== '' && currentFacilityId !== 0) ?*/}
          {/*    <div className="inspections__addModal-inner">*/}
          {/*      {*/}
          {/*        currentFacility[0]?.manuallyCreatableInspections?.length > 0 ?*/}
          {/*        currentFacility[0]?.manuallyCreatableInspections?.map((item) => (*/}
          {/*          <div onClick={() => createPeriodInspectionSubmit({period: item?.period, code: item?.code})}>*/}
          {/*            {item?.name}*/}
          {/*          </div>*/}
          {/*        ))*/}
          {/*          :*/}
          {/*          <span className="no-payment-text center">*/}
          {/*            This facility is not able to create inspections!*/}
          {/*          </span>*/}
          {/*      }*/}
          {/*    </div>*/}
          {/*    :*/}
          {/*    <span className="no-payment-text center">*/}
          {/*      First you need to  select Facility to be able to create inspections in period other*/}
          {/*    </span>*/}
          {/*}*/}
          {demandUpdatedData?.map((item, idx) => (
            <div
              onClick={() => {
                setAddStatus(false);
                setCreateFilterStatus(true);
                setCurrentDemand(item);
                setCreateFilterType(
                  item?.modal_type === 'fuel_quality_test_record'
                    ? 'fuel_quality'
                    : item?.modal_type === 'create_filters_change'
                      ? 'filter_change'
                      : 'fuel_receipt',
                );
                setFilterChangeName(item?.name);
              }}
              className='inspections__addModal-create'
              key={idx}
            >
              {item?.name}
            </div>
          ))}

          {/*<div onClick={() => {*/}
          {/*  setAddStatus(false);*/}
          {/*  setCreateFilterStatus(true);*/}
          {/*  setCreateFilterType('filter_change')*/}
          {/*}} className="inspections__addModal-create">*/}
          {/*  Create Filter Change*/}
          {/*</div>*/}
          {/*<div onClick={() => {*/}
          {/*  setAddStatus(false);*/}
          {/*  setCreateFilterStatus(true);*/}
          {/*  setCreateFilterType('fuel_quality')*/}
          {/*}} className="inspections__addModal-create">*/}
          {/*  Fuel Quality Test Record*/}
          {/*</div>*/}
        </div>
      )}
      <div className='inspections'>
        <div className='inspections__header'>
          {tabsList?.map((period, index) => (
            <p
              className={`inspections__header-tab ${
                currentTab === period?.value ? 'active' : ''
              }`}
              key={index}
              onClick={() => {
                changeTab(period?.value);
                navigate(`/inspections?tab=${period.value}`);
              }}
            >
              {period?.label}
            </p>
          ))}
        </div>
        <div className='inspections__filters'>
          <Input
            type='text'
            placeholder={t('inspections.searchPlaceholder')}
            name='inspectionsSearch'
            id='inspectionsSearch'
            image={'/assets/icons/search.svg'}
            maxwidth={204}
            value={currentSearch}
            onChange={e => {
              setCurrentSearch(e.target.value);
            }}
          />
          <div>
            {facilityOptions && (
              <Select
                defaultValue={facilityOptions[0]}
                options={facilityOptions}
                placeholder={t('inspections.filters.byFacility')}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                    borderRadius: 8,
                    width: 240,
                    height: 42,
                    backgroundColor: '#F4F5F6',
                  }),
                }}
                onChange={selected => {
                  getInspectionByFacility(selected?.value);
                  setCurrentFacilityId(selected?.value);
                }}
              />
            )}
          </div>
          <div>
            <Select
              options={statusOptions}
              placeholder={t('inspections.filters.byStatus')}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 240,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              onChange={selected => {
                setCurrentStatus(selected);
              }}
            />
          </div>
        </div>
        <div className='inspections__box'>
          <div className='inspections__table'>
            <div className='inspections__table-head'>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.date')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.inspectionId')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.facility')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.nfc')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.dueBy')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.period')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.progress')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.status')}</div>
              <div className='inspections__table-th'>{t('inspections.tableHeaders.completedBy')}</div>
            </div>
            {list?.filter(el => el.inspection_hidden === false)?.length ? (
              list
                ?.filter(el => el.inspection_hidden === false)
                ?.map(inspection => (
                  <div
                    onClick={() => {
                      navigate(`/inspections/${inspection.inspection_id}`, {
                        state: {data: inspection},
                      });
                    }}
                    className='inspections__table-row'
                    key={inspection.inspection_id}
                  >
                    <div className='inspections__table-bodyTh'>
                      {inspection?.date
                        ? moment(inspection?.date).format('ll')
                        : ''}
                    </div>
                    <div className='inspections__table-bodyTh'>
                      <div>
                        <span>{inspection.inspection_name}</span>
                        {
                          (!inspection?.equipment_is_active && inspection?.equipment_is_active !== null) && (
                            <div style={{
                              backgroundColor: '#DF98001F',
                              color: '#DF9800',
                              padding: '8px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: '500',
                              lineHeight: '16px',
                              textAlign: 'center',
                              marginTop: '8px',
                              border: '1px solid #DF9800',
                            }}>
                              {t('inspections.statusLabels.outOfService')}
                            </div>
                        )}
                      </div>
                    </div>
                    <div className='inspections__table-bodyTh'>
                      {inspection.facility_title}
                    </div>
                    <div className='inspections__table-bodyTh'>
                      <div className='inspections__table-bodyTh_icons'>
                        {inspection?.status_icons?.green_nfc_icon ||
                        inspection?.status_icons?.web_icon ||
                        inspection?.status_icons?.red_nfc_icon ? (
                          <>
                            {inspection?.status_icons?.green_nfc_icon && (
                              <div
                                className={`inspectionTags inspectionTags_NFC`}
                              >
                                <div className={`inspectionTags_NFC_icon`}>
                                  <WiFiIcon width='15' height='15' />
                                </div>
                                <div className={`inspectionTags_NFC_text`}>
                                  {t('inspections.statusLabels.nfc')}
                                </div>
                              </div>
                            )}
                            {inspection?.status_icons?.web_icon && (
                              <div
                                className={`inspectionTags inspectionTags_WEB`}
                              >
                                <div className={`inspectionTags_WEB_icon`}>
                                  <WebIcon width='17' height='17' />
                                </div>
                                <div className={`inspectionTags_WEB_text`}>
                                  {t('inspections.statusLabels.web')}
                                </div>
                              </div>
                            )}
                            {inspection?.status_icons?.red_nfc_icon && (
                              <div
                                className={`inspectionTags inspectionTags_NO_NFC`}
                              >
                                <div className={`inspectionTags_NO_NFC_icon`}>
                                  <WiFiIcon
                                    width='15'
                                    height='15'
                                    color='#DA6767'
                                  />
                                </div>
                                <div className={`inspectionTags_NO_NFC_text`}>
                                  {t('inspections.statusLabels.nfc')}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          t('inspections.messages.noDataAvailableShort')
                        )}
                      </div>
                    </div>
                    <div className='inspections__table-bodyTh'>
                      {inspection?.due_by ? inspection?.due_by : t('inspections.messages.noDataAvailable')}
                    </div>
                    <div className='inspections__table-bodyTh'>
                      {inspection?.period_label
                        ? inspection?.period_label
                        : t('inspections.messages.noDataAvailable')}
                    </div>
                    <div className='inspections__table-bodyTh'>
                      {inspection?.status === '' ? '0/0' : inspection?.status}
                    </div>
                    <div className='inspections__table-bodyTh'>
                      <span className={inspection?.status_progress + ' status'}>
                        {getTranslatedStatus(inspection?.status_progress)}
                      </span>
                    </div>
                    <div className='inspections__table-bodyTh user'>
                      {inspection?.submitted_user ? (
                        <>
                          {inspection?.submitted_user?.avatar && (
                            <img
                              className='inspections__table-img'
                              src={
                                inspection?.submitted_user?.avatar
                                  ? inspection?.submitted_user?.avatar
                                  : ''
                              }
                              alt='avatar'
                            />
                          )}
                          <span>{inspection?.submitted_user?.name}</span>
                        </>
                      ) : (
                        t('inspections.messages.noDataAvailable')
                      )}
                    </div>
                    {/*<div*/}
                    {/*  style={{ opacity: 0 }}*/}
                    {/*  className="inspections__table-bodyTh"*/}
                    {/*>*/}
                    {/*  <img src="/assets/icons/Download.svg" alt="Download" />*/}
                    {/*</div>*/}
                  </div>
                ))
            ) : (
              <div className='emptyList'>
                <img src='/assets/empty-list.png' alt='empty' />
                <p>{t('inspections.messages.noData')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
}
